import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { store } from './store';
import { useAppSelector } from './hooks/useAppSelector';
import AuthInitializer from './components/AuthInitializer';
import theme from './theme';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DoctorRegistrationPage from './pages/auth/DoctorRegistrationPage';
import OfficerRegistrationPage from './pages/auth/OfficerRegistrationPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import OfficerDashboardPage from './pages/dashboard/OfficerDashboardPage';
import DoctorDashboardPage from './pages/dashboard/DoctorDashboardPage';
import AdminDashboardPage from './pages/dashboard/AdminDashboardPage';

// Doctor Pages
import DoctorListPage from './pages/doctors/DoctorListPage';
import DoctorProfilePage from './pages/doctors/DoctorProfilePage';
import DoctorSchedulePage from './pages/doctors/DoctorSchedulePage';
import DoctorWorkflowPage from './pages/doctor/DoctorWorkflowPage';
import DoctorVisitWorkflowPage from './pages/doctor/DoctorVisitWorkflowPage';
import TodaySchedulePage from './pages/doctor/TodaySchedulePage';
import AppointmentCenterPage from './pages/doctor/AppointmentCenterPage';
import DoctorReportsPage from './pages/doctor/reports/DoctorReportsPage';
import DoctorAvailabilityPage from './pages/doctor/DoctorAvailabilityPage';
import DoctorLeavePage from './pages/doctor/DoctorLeavePage';
import DoctorNotificationsPage from './pages/doctor/DoctorNotificationsPage';

// Officer Pages
import OfficerListPage from './pages/officers/OfficerListPage';
import OfficerProfilePage from './pages/officers/OfficerProfilePage';

// Appointment Pages
import AppointmentListPage from './pages/appointments/AppointmentListPage';
import BookAppointmentPage from './pages/appointments/BookAppointmentPage';
import AppointmentDetailsPage from './pages/appointments/AppointmentDetailsPage';

// Report Pages
import VisitReportListPage from './pages/reports/VisitReportListPage';
import VisitReportDetailsPage from './pages/reports/VisitReportDetailsPage';
import CreateVisitReportPage from './pages/reports/CreateVisitReportPage';

// Prescription Pages
import PrescriptionListPage from './pages/prescriptions/PrescriptionListPage';
import PrescriptionDetailsPage from './pages/prescriptions/PrescriptionDetailsPage';

// Admin Pages
import UserManagementPage from './pages/admin/UserManagementPage';
import AdminSettingsPage from './pages/admin/SettingsPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import RealTimeOperationsPage from './pages/admin/RealTimeOperationsPage';
import DoctorManagementPage from './pages/admin/DoctorManagementPage';
import OfficerManagementPage from './pages/admin/OfficerManagementPage';
import AppointmentControlPage from './pages/admin/AppointmentControlPage';
import CaseManagementPage from './pages/admin/CaseManagementPage';
import MedicalRecordsPage from './pages/admin/MedicalRecordsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import ConfigurationPage from './pages/admin/ConfigurationPage';

// Settings Pages
import ProfilePage from './pages/settings/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';

// Phase-2 Pages (additive — no existing route altered)
import MedicalReportsPage from './pages/officer/MedicalReportsPage';
import InsurancePage from './pages/officer/InsurancePage';
import AdmissionsPage from './pages/officer/AdmissionsPage';
import InvestigationsPage from './pages/officer/InvestigationsPage';
import BloodTestBookingPage from './pages/officer/BloodTestBookingPage';
import DocumentsPage from './pages/officer/DocumentsPage';
import TimelinePage from './pages/officer/TimelinePage';
import AdmissionApprovalsPage from './pages/admin/AdmissionApprovalsPage';
import InvestigationApprovalsPage from './pages/admin/InvestigationApprovalsPage';
import InsuranceVerificationPage from './pages/admin/InsuranceVerificationPage';
import LaboratoriesPage from './pages/admin/LaboratoriesPage';
import OfficeManagementPage from './pages/admin/OfficeManagementPage';
import LabDashboardPage from './pages/lab/LabDashboardPage';
import LabInvestigationsPage from './pages/lab/LabInvestigationsPage';
import LabBloodTestsPage from './pages/lab/LabBloodTestsPage';
import StaffDashboardPage from './pages/staff/StaffDashboardPage';
import OfficeDashboardPage from './pages/office/OfficeDashboardPage';
import PrescriptionDocumentsPage from './pages/prescriptions/PrescriptionDocumentsPage';
import CoordinatorDashboardPage from './pages/dashboard/CoordinatorDashboardPage';

// Protected Route Component
const OFFICER_ROLES = ['IAS_OFFICER', 'IPS_OFFICER', 'IFS_OFFICER', 'IRS_OFFICER', 'JUDGE', 'MINISTER', 'OSD', 'SENIOR_GOVERNMENT_OFFICER'] as const;
const ADMIN_ROLES = ['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN'] as const;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && user.status !== 'ACTIVE') {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};

// Role-based Route Component
interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

const RoleRoute: React.FC<RoleRouteProps> = ({ children, allowedRoles }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthInitializer>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/register/doctor" element={<DoctorRegistrationPage />} />
                <Route path="/register/officer" element={<OfficerRegistrationPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              </Route>

              {/* Protected Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                {/* Main Dashboard */}
                <Route path="/" element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />

                {/* Officer Dashboard */}
                <Route
                  path="/officer-dashboard"
                  element={
                    <RoleRoute allowedRoles={['IAS_OFFICER', 'IPS_OFFICER', 'IFS_OFFICER', 'IRS_OFFICER', 'JUDGE', 'MINISTER', 'OSD', 'SENIOR_GOVERNMENT_OFFICER']}>
                      <OfficerDashboardPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Dashboard */}
                <Route
                  path="/doctor-dashboard"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorDashboardPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Workflow */}
                <Route
                  path="/doctor/workflow"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorWorkflowPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Today's Schedule */}
                <Route
                  path="/doctor/schedule"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <TodaySchedulePage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Appointment Center */}
                <Route
                  path="/doctor/appointments"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <AppointmentCenterPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Visit Workflow - Unified page for complete visit */}
                <Route
                  path="/doctor/visit/:appointmentId"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorVisitWorkflowPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Reports & Analytics */}
                <Route
                  path="/doctor/reports"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorReportsPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Availability Management */}
                <Route
                  path="/doctor/availability"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorAvailabilityPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Leave Management */}
                <Route
                  path="/doctor/leaves"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorLeavePage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Notification Center */}
                <Route
                  path="/doctor/notifications"
                  element={
                    <RoleRoute allowedRoles={['GOVERNMENT_DOCTOR']}>
                      <DoctorNotificationsPage />
                    </RoleRoute>
                  }
                />

                {/* Admin Dashboard */}
                <Route
                  path="/admin-dashboard"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <AdminDashboardPage />
                    </RoleRoute>
                  }
                />

                {/* Real-Time Operations Center */}
                <Route
                  path="/admin/operations"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <RealTimeOperationsPage />
                    </RoleRoute>
                  }
                />

                {/* Doctor Management */}
                <Route
                  path="/admin/doctors"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <DoctorManagementPage />
                    </RoleRoute>
                  }
                />

                {/* Officer Management */}
                <Route
                  path="/admin/officers"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <OfficerManagementPage />
                    </RoleRoute>
                  }
                />

                {/* Appointment Control Center */}
                <Route
                  path="/admin/appointments"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <AppointmentControlPage />
                    </RoleRoute>
                  }
                />

                {/* Case Management */}
                <Route
                  path="/admin/cases"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <CaseManagementPage />
                    </RoleRoute>
                  }
                />

                {/* Medical Records */}
                <Route
                  path="/admin/medical-records"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <MedicalRecordsPage />
                    </RoleRoute>
                  }
                />

                {/* Analytics & Reporting */}
                <Route
                  path="/admin/analytics"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <AnalyticsPage />
                    </RoleRoute>
                  }
                />

                {/* Notifications & Communication */}
                <Route
                  path="/admin/notifications"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <NotificationsPage />
                    </RoleRoute>
                  }
                />

                {/* Doctors */}
                <Route path="/doctors" element={<DoctorListPage />} />
                <Route path="/doctors/:id" element={<DoctorProfilePage />} />
                <Route path="/doctors/:id/schedule" element={<DoctorSchedulePage />} />

                {/* Officers */}
                <Route path="/officers" element={<OfficerListPage />} />
                <Route path="/officers/:id" element={<OfficerProfilePage />} />

                {/* Appointments */}
                <Route path="/appointments" element={<AppointmentListPage />} />
                <Route path="/appointments/book" element={<BookAppointmentPage />} />
                <Route path="/appointments/:id" element={<AppointmentDetailsPage />} />

                {/* Visit Reports */}
                <Route path="/visit-reports" element={<VisitReportListPage />} />
                <Route path="/visit-reports/:id" element={<VisitReportDetailsPage />} />
                <Route path="/visit-reports/create/:appointmentId" element={<CreateVisitReportPage />} />

                {/* Prescriptions */}
                <Route path="/prescriptions" element={<PrescriptionListPage />} />
                <Route path="/prescriptions/:id" element={<PrescriptionDetailsPage />} />

                {/* Profile & Settings */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />

                {/* Medical Records */}
                <Route path="/records" element={<PrescriptionListPage />} />
                <Route path="/records/:id" element={<PrescriptionDetailsPage />} />

                {/* Reports */}
                <Route path="/reports" element={<VisitReportListPage />} />
                <Route path="/reports/:id" element={<VisitReportDetailsPage />} />

                {/* Admin Routes */}
                <Route
                  path="/admin/users"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <UserManagementPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <SettingsPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin/audit-logs"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <AuditLogsPage />
                    </RoleRoute>
                  }
                />

                {/* Configuration & System Management */}
                <Route
                  path="/admin/configuration"
                  element={
                    <RoleRoute allowedRoles={['CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN']}>
                      <ConfigurationPage />
                    </RoleRoute>
                  }
                />

                {/* ===== Phase-2 Routes (additive) ===== */}
                {/* Officer self-service modules.
                    OFFICE_ADMIN added (additive) to each — acts on behalf of a chosen officer
                    via the OfficerSelector picker (?officerId=N). Officers keep self-profile use. */}
                <Route path="/officer/medical-reports" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'LOKBHAVAN_STAFF', 'OFFICE_ADMIN'] as unknown as string[]}><MedicalReportsPage /></RoleRoute>} />
                <Route path="/officer/insurance" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'OFFICE_ADMIN'] as unknown as string[]}><InsurancePage /></RoleRoute>} />
                <Route path="/officer/admissions" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'LOKBHAVAN_STAFF', 'OFFICE_ADMIN'] as unknown as string[]}><AdmissionsPage /></RoleRoute>} />
                <Route path="/officer/investigations" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'LOKBHAVAN_STAFF', 'OFFICE_ADMIN'] as unknown as string[]}><InvestigationsPage /></RoleRoute>} />
                <Route path="/officer/blood-tests" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'OFFICE_ADMIN'] as unknown as string[]}><BloodTestBookingPage /></RoleRoute>} />
                <Route path="/officer/documents" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'LOKBHAVAN_STAFF', 'OFFICE_ADMIN'] as unknown as string[]}><DocumentsPage /></RoleRoute>} />
                <Route path="/officer/timeline" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'OFFICE_ADMIN'] as unknown as string[]}><TimelinePage /></RoleRoute>} />

                {/* Prescription documents (multi-source) — doctor + officer + staff + admin + office_admin */}
                <Route path="/prescription-documents" element={<RoleRoute allowedRoles={[...OFFICER_ROLES, 'GOVERNMENT_DOCTOR', 'LOKBHAVAN_STAFF', 'CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN', 'OFFICE_ADMIN'] as unknown as string[]}><PrescriptionDocumentsPage /></RoleRoute>} />

                {/* Coordinator / Admin approval queues */}
                <Route path="/coordinator-dashboard" element={<RoleRoute allowedRoles={[...ADMIN_ROLES] as unknown as string[]}><CoordinatorDashboardPage /></RoleRoute>} />
                <Route path="/admin/admissions" element={<RoleRoute allowedRoles={['OFFICE_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><AdmissionApprovalsPage /></RoleRoute>} />
                <Route path="/admin/investigations" element={<RoleRoute allowedRoles={['OFFICE_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><InvestigationApprovalsPage /></RoleRoute>} />
                <Route path="/admin/insurance" element={<RoleRoute allowedRoles={['OFFICE_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><InsuranceVerificationPage /></RoleRoute>} />
                <Route path="/admin/laboratories" element={<RoleRoute allowedRoles={[...ADMIN_ROLES, 'LAB_ADMIN'] as unknown as string[]}><LaboratoriesPage /></RoleRoute>} />

                {/* Lab admin */}
                <Route path="/lab-dashboard" element={<RoleRoute allowedRoles={['LAB_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><LabDashboardPage /></RoleRoute>} />
                <Route path="/lab/investigations" element={<RoleRoute allowedRoles={['LAB_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><LabInvestigationsPage /></RoleRoute>} />
                <Route path="/lab/blood-tests" element={<RoleRoute allowedRoles={['LAB_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><LabBloodTestsPage /></RoleRoute>} />
                <Route path="/lab/catalog" element={<RoleRoute allowedRoles={['LAB_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><LaboratoriesPage /></RoleRoute>} />

                {/* LokBhavan staff + Office admin */}
                <Route path="/staff-dashboard" element={<RoleRoute allowedRoles={['LOKBHAVAN_STAFF'] as unknown as string[]}><StaffDashboardPage /></RoleRoute>} />
                <Route path="/office-dashboard" element={<RoleRoute allowedRoles={['OFFICE_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><OfficeDashboardPage /></RoleRoute>} />
                <Route path="/office-management" element={<RoleRoute allowedRoles={['OFFICE_ADMIN', ...ADMIN_ROLES] as unknown as string[]}><OfficeManagementPage /></RoleRoute>} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthInitializer>
      </ThemeProvider>
    </Provider>
  );
}

export default App;